//src/admin/components/AdminCommandPalette.js
import React, { useEffect, useState, useRef } from "react";
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function AdminCommandPalette({ open, setOpen, pages, lang }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);

  const TEXT = {
    EN: {
      placeholder: "Search pages...",
      noResults: "No results"
    },
    AR: {
      placeholder: "ابحث عن صفحة...",
      noResults: "لا يوجد نتائج"
    }
  }[lang];

  const getName = (p) => p.name[lang] || p.name.EN;

  /* ---------- FUZZY SEARCH ---------- */

  const fuzzy = (str, q) => {
    str = str.toLowerCase();
    q = q.toLowerCase();

    let i = 0;

    for (let char of str) {
      if (char === q[i]) i++;
      if (i === q.length) return true;
    }

    return false;
  };

  /* ---------- SMART ENTITY SEARCH ---------- */

const smartCommand = () => {
  const q = query.trim().toLowerCase();

  const parts = q.split(" ");

  if (parts.length < 2) return null;

  const type = parts[0];
  const id = parts[1];

  if (!id) return null;

  if (type === "order" || type === "طلب") {
    return {
      label: `Open order #${id}`,
      action: () => navigate(`/admin/orders/${id}`)
    };
  }

  if (type === "product" || type === "منتج") {
    return {
      label: `Open product #${id}`,
      action: () => navigate(`/admin/products/edit/${id}`)
    };
  }

  if (type === "customer" || type === "عميل") {
    return {
      label: `Open customer #${id}`,
      action: () => navigate(`/admin/customers/${id}`)
    };
  }

  return null;
};

const command = smartCommand();

  const results = pages.filter((p) => {
    const name = getName(p);

    return (
      name.toLowerCase().includes(query.toLowerCase()) ||
      fuzzy(name, query)
    );
  });

  /* ---------- OPEN PALETTE ---------- */

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  /* ---------- KEYBOARD NAVIGATION ---------- */

  useEffect(() => {
    const handler = (e) => {
      if (!open) return;

      if (e.key === "Escape") {
        setOpen(false);
      }

      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        setSelected((s) => Math.min(s + 1, results.length - 1));
      }

      if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        setSelected((s) => Math.max(s - 1, 0));
      }

      if (e.key === "Enter") {

  if (command) {
    command.action();
    setOpen(false);
    return;
  }

  if (results[selected]) {
    navigate(results[selected].path);
    setOpen(false);
  }
}
    };

    window.addEventListener("keydown", handler);

    return () => window.removeEventListener("keydown", handler);
  }, [results, selected, open]);

  /* ---------- HIGHLIGHT MATCH ---------- */

  const highlight = (text, isSelected) => {
  if (!query) return text;

  const regex = new RegExp(`(${query})`, "gi");

  return text.split(regex).map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <span
        key={i}
        className={
          isSelected
            ? "text-white underline font-semibold"
            : "text-[#008060] font-semibold"
        }
      >
        {part}
      </span>
    ) : (
      part
    )
  );
};

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[999] flex items-start justify-center pt-24"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-2xl bg-white text-gray-800 rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* SEARCH BAR */}

        <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-200">
          <FaSearch className="text-gray-400" />

          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={TEXT.placeholder}
            className="flex-1 outline-none text-sm text-gray-700"
          />
        </div>

        {/* PILLS */}

        <div className="p-4 flex flex-wrap gap-2 max-h-96 overflow-y-auto">

  {command && (
    <button
      onClick={() => {
        command.action();
        setOpen(false);
      }}
      className="px-3 py-1.5 rounded-full text-sm border
                 bg-[#008060] text-white border-[#008060]"
    >
      {command.label}
    </button>
  )}

  {results.map((page, i) => (
            <button
              key={i}
              onClick={() => {
                navigate(page.path);
                setOpen(false);
              }}
              className={`
                px-3 py-1.5 rounded-full text-sm border transition
                ${
                  i === selected
                    ? "bg-[#008060] text-white border-[#008060]"
                    : "bg-[#f6f6f7] text-[#202223] border-[#e1e3e5] hover:bg-[#ededee]"
                }
              `}
            >
              {highlight(getName(page), i === selected)}
            </button>
          ))}

          {results.length === 0 && (
            <div className="text-sm text-gray-500">
              {TEXT.noResults}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminCommandPalette;